import i18nFactory from '@yandex-int/i18n';
import { Crop, PixelCrop } from 'react-image-crop';

import { Size, ValidationErrorData } from '../../typings/common';

import * as keyset from './CreativeImageCropHelper.i18n';

const i18n = i18nFactory(keyset);

export const getCroppedCanvas = (
    pixelCrop: PixelCrop,
    image: HTMLImageElement,
    sizeOptions: Size
): HTMLCanvasElement | undefined => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = sizeOptions.width;
    canvas.height = sizeOptions.height;

    if (!ctx) {
        return;
    }

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        sizeOptions.width,
        sizeOptions.height
    );

    return canvas;
};

export const getCroppedImageBlob = (
    pixelCrop: PixelCrop,
    image: HTMLImageElement,
    sizeOptions: Size
): Promise<Blob> => {
    const canvas = getCroppedCanvas(pixelCrop, image, sizeOptions);

    if (!canvas) {
        return Promise.reject();
    }

    return new Promise<Blob>((resolve, reject): void => {
        canvas.toBlob(
            (blob: Blob | null) => {
                if (!blob) {
                    reject();

                    return;
                }

                resolve(blob);
            },
            'image/jpeg',
            0.95
        );
    });
};

export const convertCropToPixelCrop = (crop: Crop, imageSize: Size): PixelCrop => {
    return {
        x: Math.round((crop.x * imageSize.width) / 100),
        y: Math.round((crop.y * imageSize.height) / 100),
        width: Math.round(((crop.width || 0) * imageSize.width) / 100),
        height: Math.round(((crop.height || 0) * imageSize.height) / 100)
    };
};

export const getFullImageCrop = (imageSize: Size): PixelCrop => {
    return {
        x: 0,
        y: 0,
        width: imageSize.width,
        height: imageSize.height
    };
};

export const getScaledImageSize = (imageSize: Size, containerSize: Size): Partial<Size> => {
    const aspect = imageSize.width / imageSize.height;
    return {
        height: aspect > 1 ? containerSize.width / aspect : containerSize.height
    };
};

export const getScaledImageCrop = (naturalImageSize: Size, realImageSize: Size, crop: PixelCrop): PixelCrop => {
    const scale = naturalImageSize.width / realImageSize.width;

    if (scale < 1) {
        return {
            x: crop.x * scale,
            y: crop.y * scale,
            height: crop.height * scale,
            width: crop.width * scale
        };
    }

    return crop;
};

export const getCreativeImageCropError = (message?: string): ValidationErrorData => {
    if (!message) {
        return {
            text: i18n('request-error')
        };
    }

    return {
        text: message
    };
};

export const convertCropToPercentCrop = (size: Size, pixelCrop: PixelCrop): Crop => {
    return {
        x: (pixelCrop.x * 100) / size.width,
        y: (pixelCrop.y * 100) / size.height,
        height: (pixelCrop.height * 100) / size.height,
        width: (pixelCrop.width * 100) / size.width
    };
};

export const getImageSizes = (src: string): Promise<Size> => {
    return new Promise<Size>((resolve, reject): void => {
        const image = new Image();
        image.addEventListener('load', () => {
            resolve({
                width: image.naturalWidth,
                height: image.naturalHeight
            });
        });
        image.addEventListener('error', () => {
            reject({ message: i18n('image-download-error') });
        });
        image.src = src;
    });
};
